/**
 * The `TimePicker`'s strings, read once from `common`.
 *
 * Rule 1 of the design system says no text inside a component, which leaves
 * every call site spelling out ten labels. Four screens doing that is four
 * chances for the Arabic meridiem to end up "AM" on one of them — and ص/م is
 * not something a component can infer from the locale, so it has to be passed.
 * This is the one place the keys are named.
 *
 * A hook rather than a constant: `t` is only available inside a component, and
 * the labels have to re-resolve when the language changes without a reload
 * (NFR-I3).
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { type TimePickerLabels } from './TimePicker'

export function useTimePickerLabels(): TimePickerLabels {
  const { t, i18n } = useTranslation('common')

  return useMemo(
    () => ({
      placeholder: t('timePicker.placeholder'),
      dialog: t('timePicker.dialog'),
      dial: t('timePicker.dial'),
      hour: t('timePicker.hour'),
      minute: t('timePicker.minute'),
      am: t('timePicker.am'),
      pm: t('timePicker.pm'),
      confirm: t('timePicker.confirm'),
      cancel: t('timePicker.cancel'),
      clear: t('timePicker.clear'),
    }),
    // `resolvedLanguage` rather than `t`: i18next hands back a stable `t`
    // reference across a language change, so keying on it alone would keep the
    // English labels on screen after a switch to Arabic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.resolvedLanguage],
  )
}
